"use strict";
/**
 * Plan 59 Phase 5a/5b — FormSpec helpers for the HDS x CMC integration.
 *
 * The FormSpec is HDS's data-set template (title, description, consent,
 * permissions, sections, custom-field/existing-stream refs, features).
 * It lives independently of CMC, but is consumed at invite-time to
 * derive the CMC `requestedPermissions` and is mirrored as a snapshot
 * onto the per-invite trigger event content (Q-F6 lock: snapshot-frozen).
 *
 * Storage (Q-F5 lock: hds-form-spec/v1 — slash separator required by Pryv events.get types regex):
 * - **Doctor side**: one canonical template event per data-set, stored
 *   on the doctor's `:_cmc:apps:hds-collector:<collectorId>` scope stream.
 * - **Per-invite snapshot**: doctor's `consent/request-cmc` trigger event
 *   carries `content.hdsFormSpec: <full snapshot>` (Candidate C). Q-F2
 *   lock confirms the plugin accepts unknown content keys.
 * - **Patient side**: mirrored to the patient's own `consent/accept-cmc`
 *   event content via `mirrorFormSpecOnAcceptEvent` post-accept (post-
 *   acceptInvite the trigger event becomes unreachable from the patient
 *   side, so the snapshot must live with the patient's accept event).
 *
 * Chat-only data sets (Q-F4 lock): when a FormSpec has no real
 * permissions, `deriveCmcPermissions` injects the `:hds:noop` placeholder
 * so `cmc.createInvite`'s non-empty-permissions check passes. The
 * patient's hds-webapp provisions `:hds:noop` on first launch.
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.HDS_NOOP_PERMISSION = exports.HDS_NOOP_STREAM_ID = exports.FORM_SPEC_EVENT_TYPE = void 0;
exports.formSpecFingerprint = formSpecFingerprint;
exports.formSpecForPerson = formSpecForPerson;
exports.validateFormSpecItemKeys = validateFormSpecItemKeys;
exports.saveFormSpec = saveFormSpec;
exports.loadFormSpec = loadFormSpec;
exports.eventToFormSpecRecord = eventToFormSpecRecord;
exports.listFormSpecs = listFormSpecs;
exports.getFormSpecById = getFormSpecById;
exports.createInviteWithFormSpec = createInviteWithFormSpec;
exports.mirrorFormSpecOnAcceptEvent = mirrorFormSpecOnAcceptEvent;
exports.readOfferWithFormSpec = readOfferWithFormSpec;
exports.deriveCmcPermissions = deriveCmcPermissions;
exports.provisionHdsNoop = provisionHdsNoop;
exports.isChatOnlyFormSpec = isChatOnlyFormSpec;
const patchedPryv_ts_1 = require("../patchedPryv.js");
const logger = __importStar(require("../logger.js"));
const HDSModelInitAndSingleton_ts_1 = require("../HDSModel/HDSModelInitAndSingleton.js");
const constants_ts_1 = require("./constants.js");
/**
 * Event type for the canonical FormSpec template event on the doctor side.
 *
 * Must satisfy Pryv's events.get `types` filter regex
 * `^(series:)?[a-z0-9-]+/(\*|[a-z0-9-]+)$` — i.e. `<category>/<name>` with
 * `/` (not `:`) separator. Original Plan 59 Phase 5a value was
 * `'hds:form-spec-v1'` which `events.create` accepted silently but
 * `events.get` rejected with `invalid-parameters-format` when used in a
 * types filter; corrected to slash form in plan 61 C1.a verification
 * (Round 7, 2026-05-27). No production data was migrated — the only
 * consumer of saveFormSpec was added in C1.a itself.
 */
exports.FORM_SPEC_EVENT_TYPE = 'hds-form-spec/v1';
/** The HDS no-op permission stream — see Q-F3/F4 in the FormSpec design brief. */
exports.HDS_NOOP_STREAM_ID = 'hds-noop';
/** Permission level used by the chat-only placeholder. Read on an empty stream is a no-op. */
exports.HDS_NOOP_PERMISSION = { streamId: exports.HDS_NOOP_STREAM_ID, level: 'read' };
/**
 * `sha256:<hex>` of a FormSpec's content, excluding `source` and `openLink` (bookkeeping,
 * not part of what an invite shares). Stable under object key order.
 */
async function formSpecFingerprint(formSpec) {
    const { source: _s, openLink: _o, ...content } = formSpec;
    const json = JSON.stringify(content, (_k, val) => {
        if (val == null || typeof val !== 'object' || Array.isArray(val))
            return val;
        return Object.fromEntries(Object.keys(val).sort().map(k => [k, val[k]]));
    });
    const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(json));
    return 'sha256:' + [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
}
/**
 * The copy of a FormSpec that may leave the doctor's account: without `openLink`, whose
 * `capabilityUrl` is the permanent link itself (data-model CUSTOM-FIELDS-AND-SYSTEM.md, 3.41.0).
 * Used for the invite snapshot and the data set carried by a scope request.
 */
function formSpecForPerson(formSpec) {
    const { openLink: _o, ...rest } = formSpec;
    return rest;
}
/**
 * Check every `sections[].itemKeys` entry of a FormSpec against the published
 * data-model.
 *
 * **Why this exists.** FormSpecs reference item keys *directly*, while events
 * resolve through `forEvent(streamId + eventType)`. An item rename that keeps
 * streamId and eventType therefore orphans nothing in stored events and looks
 * safe — but it silently strands every spec naming the old key. That is exactly
 * how data-model 2.3.0 (`b867215`) withdrew the `fertility-hormone-{fsh,hcg,pdg,e3g}`
 * rename aliases on the reasoning that "no stored event referenced the item key",
 * which was true of events and false of specs. Consumers now skip unresolvable
 * keys rather than throwing (`B-2026-09-03-3`), so the failure is silent: the
 * item simply never appears in the form and the subject cannot enter it.
 *
 * Read-only and model-only — it does not look at stored data. Returns `[]` for a
 * clean spec, so the result doubles as a boolean.
 *
 * @param formSpec The spec to check.
 * @param model Defaults to the initialised singleton. Pass one explicitly in tests.
 */
function validateFormSpecItemKeys(formSpec, model) {
    const m = model ?? (0, HDSModelInitAndSingleton_ts_1.getModel)();
    const issues = [];
    const activeByStreamId = new Map();
    for (const def of m.itemsDefs.getAllActive()) {
        const streamId = def.data?.streamId;
        if (typeof streamId !== 'string')
            continue;
        const list = activeByStreamId.get(streamId);
        if (list)
            list.push(def.key);
        else
            activeByStreamId.set(streamId, [def.key]);
    }
    for (const section of (formSpec.sections ?? [])) {
        for (const itemKey of (section.itemKeys ?? [])) {
            const def = m.itemsDefs.forKey(itemKey, false);
            if (def == null) {
                issues.push({ sectionKey: section.key, itemKey, reason: 'unknown' });
                continue;
            }
            if (def.isSystem) {
                issues.push({ sectionKey: section.key, itemKey, reason: 'system' });
                continue;
            }
            if (!def.isDeprecated)
                continue;
            // Only suggest a replacement when it is unambiguous. Several active items
            // can share a streamId (they differ by eventType), and picking one of those
            // would be a guess that changes the unit data is recorded in.
            const candidates = activeByStreamId.get(def.data?.streamId) ?? [];
            const issue = { sectionKey: section.key, itemKey, reason: 'deprecated' };
            if (candidates.length === 1)
                issue.replacement = candidates[0];
            issues.push(issue);
        }
    }
    return issues;
}
/** One-line rendering of an issue, shared by the save-time warning and callers. */
function describeItemKeyIssue(issue) {
    const where = `${issue.sectionKey}/${issue.itemKey}`;
    if (issue.reason === 'unknown')
        return `${where} (not defined by the published data-model)`;
    if (issue.reason === 'system')
        return `${where} (system item — not a form field)`;
    return issue.replacement
        ? `${where} (deprecated — use ${issue.replacement})`
        : `${where} (deprecated)`;
}
/**
 * Idempotent upsert of the canonical FormSpec event on the doctor's CMC
 * scope stream. One event per data-set. Re-saving with the same
 * collectorScopeStreamId updates the existing event in place.
 *
 * Item keys are checked against the published data-model and any problem is
 * **logged, not thrown** — a collector re-saving an already-stale spec must not
 * be locked out of their own data set, and the authoring UI is the right place
 * to act on {@link validateFormSpecItemKeys} before it gets this far.
 *
 * @param connection Doctor's master connection.
 * @param collectorScopeStreamId `:_cmc:apps:hds-collector:<collectorId>` (must already exist).
 * @param formSpec The FormSpec to persist.
 * @returns The created or updated event.
 */
async function saveFormSpec(connection, collectorScopeStreamId, formSpec) {
    if (formSpec.version !== 1) {
        throw new Error(`saveFormSpec: unsupported FormSpec version ${formSpec.version}`);
    }
    // Advisory only, and skipped outright when the caller never ran initHDSModel:
    // there is nothing to check against, and a save must not depend on it. Any
    // failure inside the validator itself is a real bug and is left to propagate.
    const model = (0, HDSModelInitAndSingleton_ts_1.getModel)();
    if (model.isLoaded) {
        const issues = validateFormSpecItemKeys(formSpec, model);
        if (issues.length > 0) {
            logger.warn(`saveFormSpec: ${issues.length} item key(s) in "${collectorScopeStreamId}" do not resolve ` +
                `cleanly against the published data-model — ${issues.map(describeItemKeyIssue).join(', ')}`);
        }
    }
    const existing = await loadFormSpec(connection, collectorScopeStreamId);
    if (existing) {
        return await connection.apiOne('events.update', {
            id: existing.id,
            update: { content: formSpec }
        }, 'event');
    }
    return await connection.apiOne('events.create', {
        streamIds: [collectorScopeStreamId],
        type: exports.FORM_SPEC_EVENT_TYPE,
        content: formSpec
    }, 'event');
}
/**
 * Read the canonical FormSpec event from the doctor's CMC scope stream.
 * Returns null if no event exists yet (fresh data-set).
 */
async function loadFormSpec(connection, collectorScopeStreamId) {
    const events = await connection.apiOne('events.get', {
        streams: [collectorScopeStreamId],
        types: [exports.FORM_SPEC_EVENT_TYPE],
        limit: 1
    }, 'events');
    return (events && events.length > 0) ? events[0] : null;
}
/**
 * Pure helper: lift a raw `hds-form-spec/v1` event onto its scope-stream
 * `collectorId`. Exported for unit-testing in isolation from the I/O layer.
 *
 * @param event   a `hds-form-spec/v1` event read from a `:_cmc:apps:<appCode>:*` stream.
 * @param appCode defaults to `CMC_APP_CODES.COLLECTOR`.
 */
function eventToFormSpecRecord(event, appCode = constants_ts_1.CMC_APP_CODES.COLLECTOR) {
    const streamIds = (event.streamIds || []);
    const marker = ':_cmc:apps:' + appCode + ':';
    const subScope = streamIds.find(s => s.indexOf(marker) !== -1) ?? streamIds[0] ?? '';
    const collectorId = (0, constants_ts_1.extractAppSubScopeSuffix)(subScope, appCode);
    return {
        collectorId,
        formSpec: event.content,
        event
    };
}
/**
 * Doctor-side: list every FormSpec the doctor owns under their
 * `:_cmc:apps:<appCode>` scope (one per data-set). Every result is
 * "published" — saveFormSpec writes the canonical event immediately
 * (no draft state in the CMC model).
 *
 * Phase C (plan 61): replaces `appManaging.getCollectors()`.
 */
async function listFormSpecs(connection, opts) {
    const appCode = opts?.appCode ?? constants_ts_1.CMC_APP_CODES.COLLECTOR;
    const parentStream = patchedPryv_ts_1.cmc.appScope(appCode);
    const events = await connection.apiOne('events.get', {
        streams: [parentStream],
        types: [exports.FORM_SPEC_EVENT_TYPE],
        limit: opts?.limit ?? 1000
    }, 'events');
    return (events ?? []).map(e => eventToFormSpecRecord(e, appCode));
}
/**
 * Doctor-side: load a single FormSpec by its `collectorId`. Returns null
 * if no FormSpec event exists yet on `:_cmc:apps:<appCode>:<collectorId>`.
 *
 * Phase C (plan 61): replaces `appManaging.getCollectorById()`.
 */
async function getFormSpecById(connection, collectorId, opts) {
    const appCode = opts?.appCode ?? constants_ts_1.CMC_APP_CODES.COLLECTOR;
    const subScope = (0, constants_ts_1.appSubScope)(appCode, collectorId);
    const event = await loadFormSpec(connection, subScope);
    if (!event)
        return null;
    return eventToFormSpecRecord(event, appCode);
}
/**
 * Doctor-side: mint a CMC invite with the FormSpec snapshot embedded on
 * the trigger event content at events.create time.
 *
 * **Critical:** this is the ONLY way to get the snapshot to propagate
 * to the patient's capability access. The CMC plugin's capability-mint
 * hook copies the trigger event content into a separate offer event
 * (under `:_cmc:_internal:offer:<capId>`) AT MINT TIME — once. Later
 * `events.update` on the trigger event does NOT update the offer event,
 * so `cmc.readOffer` (which reads the offer event) won't see anything
 * stamped post-mint. (Verified by `verify-formspec.mjs` 2026-05-21.)
 *
 * Therefore we bypass `cmc.createInvite` (which doesn't accept arbitrary
 * extra content keys) and call `events.create` directly with `hdsFormSpec`
 * already in content. The capability-mint hook fires inside the
 * events.create chain, copies the content (including hdsFormSpec) to
 * the offer event, and stamps capabilityUrl + capabilityAccessId +
 * capabilityExpiresAt on the returned event.
 *
 * @returns the same shape as `cmc.createInvite` for caller compatibility.
 */
async function createInviteWithFormSpec(connection, params) {
    if (params.expiresAt === null && params.mode !== 'open-link') {
        throw new Error('createInviteWithFormSpec: expiresAt null (no expiry) requires mode "open-link"');
    }
    const requesterMeta = Object.assign({ displayName: params.displayName, appId: params.appCode }, params.requesterMeta ?? {});
    const request = {
        title: params.title ?? params.formSpec.title ?? { en: params.displayName },
        description: params.description ?? params.formSpec.description ?? { en: '' },
        consent: params.consent ?? params.formSpec.consent ?? { en: '' },
        permissions: params.requestedPermissions
    };
    if (params.features)
        request.features = params.features;
    if (params.expiresAt === null)
        request.expiresAt = null;
    else if (params.expiresAt)
        request.expiresAt = params.expiresAt;
    if (params.accessType)
        request.accessType = params.accessType;
    const content = {
        to: params.to === undefined ? null : params.to,
        capabilityRequested: true,
        request,
        requesterMeta,
        // HDS-extension: snapshot the FormSpec on the trigger event content
        // so the capability-mint hook copies it into the offer event.
        hdsFormSpec: formSpecForPerson(params.formSpec)
    };
    if (params.mode && params.mode !== 'single-use') {
        content.capability = { mode: params.mode };
    }
    const event = await connection.apiOne('events.create', {
        streamIds: [params.scopeStreamId],
        type: constants_ts_1.CMC_EVENT_TYPES.INVITE_TRIGGER,
        content
    }, 'event');
    return {
        inviteEventId: event.id,
        capabilityUrl: event?.content?.capabilityUrl,
        mode: event?.content?.capability?.mode ?? params.mode ?? 'single-use',
        expiresAt: event?.content?.capabilityExpiresAt ?? event?.content?.request?.expiresAt
    };
}
/**
 * Patient-side: mirror the FormSpec snapshot onto the patient's own
 * `consent/accept-cmc` event content post-accept. Without this the
 * snapshot is unreachable on the patient side (the doctor's trigger
 * event is gone behind the consumed capability).
 *
 * @param connection Patient's master connection.
 * @param acceptEventId The patient's accept event id (from `cmc.acceptInvite`'s return).
 * @param formSpec Snapshot read pre-accept (e.g. via `readOfferWithFormSpec`).
 */
async function mirrorFormSpecOnAcceptEvent(connection, acceptEventId, formSpec) {
    const current = await connection.apiOne('events.getOne', {
        id: acceptEventId
    }, 'event');
    const mergedContent = { ...(current?.content || {}), hdsFormSpec: formSpec };
    return await connection.apiOne('events.update', {
        id: acceptEventId,
        update: { content: mergedContent }
    }, 'event');
}
/**
 * Patient-side: pre-accept, read the full offer trigger-event content
 * including any `hdsFormSpec` snapshot. The SDK's `cmc.readOffer` filters
 * to a fixed shape and drops HDS-extension fields, so this is a thin
 * variant that returns the raw content.
 *
 * Single-use capabilities consume on accept — call this BEFORE `cmc.acceptInvite`.
 */
async function readOfferWithFormSpec(capabilityUrl, opts) {
    const pryvMod = (opts && opts.pryv) || patchedPryv_ts_1.pryv;
    const cap = new pryvMod.Connection(capabilityUrl);
    const events = await cap.apiOne('events.get', {
        types: [constants_ts_1.CMC_EVENT_TYPES.INVITE_TRIGGER],
        limit: 1
    }, 'events');
    if (!events || events.length === 0) {
        throw new Error('readOfferWithFormSpec: capability offer stream empty');
    }
    const content = events[0]?.content || {};
    return {
        content,
        hdsFormSpec: content.hdsFormSpec ?? null
    };
}
/**
 * Derive the `requestedPermissions` array for `cmc.createInvite` from a
 * FormSpec. If the FormSpec has no real permissions (chat-only data set),
 * inject the `hds-noop` placeholder so the CMC schema's non-empty-
 * permissions check passes (Q-F4 lock).
 *
 * The placeholder grants `read` on an empty patient-side stream — a
 * functional no-op. The patient's hds-webapp provisions `hds-noop` at
 * first launch (Q-F3: patient-only).
 *
 * Note: the stream is plain `hds-noop` (no colon prefix) — `:hds:` is
 * not a registered system-stream namespace on open-pryv.io, so the
 * api-server's streams.create rejects `:hds:noop` with `invalid-request-
 * structure`. A regular user stream works fine.
 */
function deriveCmcPermissions(formSpec) {
    const perms = (formSpec.permissions || []).filter(p => p && p.streamId && p.level);
    if (perms.length > 0)
        return perms;
    return [{ ...exports.HDS_NOOP_PERMISSION }];
}
/**
 * Patient-side: provision the `hds-noop` stream used by the chat-only
 * placeholder permission. Idempotent — re-runs OK.
 */
async function provisionHdsNoop(connection) {
    try {
        await connection.apiOne('streams.create', {
            id: exports.HDS_NOOP_STREAM_ID,
            name: 'HDS no-op (CMC chat-only placeholder)'
        });
    }
    catch (e) {
        const code = e?.innerObject?.id ?? e?.id;
        if (code === 'item-already-exists')
            return;
        throw e;
    }
}
/**
 * Doctor-side helper: did the FormSpec produce a chat-only invite (i.e.
 * its only granted permission is `:hds:noop`)? Used by listInviteRecordsFor
 * + PatientsTable to surface "chat-only relationship" badges.
 */
function isChatOnlyFormSpec(formSpec) {
    if (!formSpec)
        return false;
    const perms = (formSpec.permissions || []).filter(p => p && p.streamId && p.level);
    return perms.length === 0;
}
